import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useMemo, useState } from 'react';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { Box, Popover } from '@mui/material';
import * as Sentry from '@sentry/browser';
import { useAtom, useAtomValue } from 'jotai';
import Button from '@/components/shared/Button';
import { TestIds } from '../../../../cypress/testIds';
import { UserAvatar } from '../UserAvatar';
import { SystemPopover } from '../../SystemRequirement/SystemPopover';
import { UserSettings } from './UserSettings';
import * as state from '@/state/atoms';
import { NavbarLinks } from '@/components/Analysis/shared/AnalysisNavbar/styles';

const placeHolderTitle = 'Abyss User';

export const LogInOrOut = () => {
  const { user, loginWithRedirect, isLoading } = useAuth0();
  const auth0Token = useAtomValue(state.auth0TokenState);
  const [anchorElementUser, setAnchorElementUser] = useState<undefined | HTMLElement>(undefined);
  const [anchorElementSystem, setAnchorElementSystem] = useState<undefined | HTMLElement>(
    undefined
  );
  const [isSystemPopoverOpened, setIsSystemPopoverOpened] = useAtom(state.isSystemPopoverOpened);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElementUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElementUser(undefined);
  };

  const handleCloseSystemMenu = () => {
    setAnchorElementSystem(undefined);
    setIsSystemPopoverOpened(false);
  };

  useEffect(() => {
    Sentry.setUser({
      email: user?.email ?? '<EMAIL_NOT_FOUND>',
      id: user?.sub ?? '<SUB_NOT_FOUND>',
    });
  }, [user]);

  const userLoggedIn = useMemo(() => {
    return (
      <Stack direction="row" sx={{ cursor: 'pointer' }} spacing={2} alignItems="center">
        <Stack direction="column">
          <Typography sx={{ lineHeight: 'normal' }} variant="subtitle2">
            {user?.name || 'Fabric User'}
          </Typography>
          <Typography sx={{ lineHeight: 'normal' }} variant="caption">
            {placeHolderTitle}
          </Typography>
        </Stack>
        <UserAvatar
          name={user?.name}
          picture={user?.picture}
          sx={{ height: '32px', width: '32px' }}
        />
      </Stack>
    );
  }, [user?.name, user?.picture]);

  if (!user || !auth0Token) {
    return (
      <Button
        size="large"
        variant="text"
        data-test-id={TestIds.LOGIN_INPUT}
        onClick={() => loginWithRedirect()}
        disabled={isLoading}
        sx={NavbarLinks}
      >
        Log in
      </Button>
    );
  }

  return (
    <>
      <Box onClick={handleOpenUserMenu}>{userLoggedIn}</Box>
      <Popover
        sx={{ mt: '45px', padding: '1%' }}
        anchorEl={anchorElementUser}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorElementUser)}
        onClose={handleCloseUserMenu}
        PaperProps={{
          sx: {
            borderRadius: '3%',
            left: 'unset !important',
            right: '24px',
          },
        }}
      >
        <UserSettings handleCloseUserMenu={handleCloseUserMenu} />
      </Popover>
      <SystemPopover
        anchorElementSystem={anchorElementSystem}
        open={isSystemPopoverOpened}
        handleCloseSystemMenu={handleCloseSystemMenu}
      />
    </>
  );
};
